import { createRouter } from '@tanstack/react-router';
import { QueryClient } from '@tanstack/react-query';
import { routeTree } from '../routeTree.gen';
import { trackPageView, trackNavigation } from './analytics';

/**
 * Router Configuration
 * 
 * Implements Requirements 6.1, 6.2, 6.3, 19.2:
 * - TanStack Router for type-safe routing
 * - Nested routes with layout composition
 * - Automatic code splitting per route
 * - Page view and navigation tracking
 * 
 * This file configures the TanStack Router instance with:
 * - QueryClient context for data fetching
 * - Route tree generated from file-based routing
 * - Default preload behavior
 * - Analytics tracking on navigation
 */

/**
 * Create and configure the router instance
 * 
 * @param queryClient - TanStack Query client for data fetching
 * @returns Configured router instance
 */
export function createAppRouter(queryClient: QueryClient) {
  const router = createRouter({
    routeTree,
    context: {
      queryClient,
    },
    defaultPreload: 'intent', // Preload on hover/focus
    defaultPreloadStaleTime: 0, // Always preload fresh data
  });

  // Track navigation events (Requirement 19.2)
  let previousPath: string | null = null;
  
  router.subscribe('onLoad', (event) => {
    const currentPath = event.toLocation.pathname;
    
    // Track page view
    trackPageView(currentPath, {
      search: event.toLocation.search,
      hash: event.toLocation.hash,
    });
    
    // Track navigation if we have a previous path
    if (previousPath && previousPath !== currentPath) {
      trackNavigation(previousPath, currentPath);
    }
    
    previousPath = currentPath;
  });

  return router;
}

// Infer the router type for use in other files
export type AppRouter = ReturnType<typeof createAppRouter>;

// Register the router type for type-safe navigation
declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
